import * as Enums from '@/enums.ts';

export const USER_ID_CUTOFF = 5;

export const CENTS_DOLLARS_MULTIPLIER = 100; // Useful to have when moving into other currencies.

export const VIEW_ONLY_STATION_CODE = 'ZZ';
export const GENERAL_TAKEOUT_CODE = 'ZY';
export const GENERAL_DELIVERY_CODE = 'ZT';
export const TAKEOUT_STATION_CODES = ['ZU', 'ZV', 'ZW', 'ZX', 'ZY'];
export const DELIVERY_STATION_CODES = ['ZP', 'ZQ', 'ZR', 'ZS', 'ZT'];
export const TAKEOUT_STATION_IDS = [1290, 1291, 1292, 1293, 1294];
export const DELIVERY_STATION_IDS = [1285, 1286, 1287, 1288, 1289];
export const ALL_RESERVED_STATION_IDS = TAKEOUT_STATION_IDS.concat(DELIVERY_STATION_IDS);
export const ALL_RESERVED_STATION_CODES = TAKEOUT_STATION_CODES.concat(DELIVERY_STATION_CODES);
export const DEMO_LOCATION_CODES = ['0', '9', 'O', 'U'];
export const TEST_LOCATION_CODES = ['B'];
export const EXCHANGE_OBJECT_EXPIRY_TIME_IN_HOURS = 6;
export const EXCHANGE_OBJECT_EXPIRY_TIME_IN_SECONDS = 3600 * EXCHANGE_OBJECT_EXPIRY_TIME_IN_HOURS; // 5 hours.

export const allRoutes = {
  menu: '/menu',
  order: '/order',
  messages: '/messages',
  pay: '/pay',
  getApp: '/get-app',
  info: '/info',
  settings: '/settings',
  locationSelection: '/location-selection',
  menuSelection: '/menu-selection',
  demoMenuModal: '/demo-menu-modal',
  menuItemModal: '/menu-item-modal',
  filtersModal: '/filters-modal',
  messageOptionsModal: '/message-options-modal',
  termsOfServiceModal: '/terms-of-service-modal',
  privacyPolicyModal: '/privacy-policy-modal',
  cookiePolicyModal: '/cookie-policy-modal',
  aboutUsModal: '/about-us-modal',
  howItWorksModal: '/how-it-works-modal',
  whatIsNfc: '/what-is-nfc',
  billDetailsModal: '/bill-details-modal',
  billModal: '/bill-modal',
  pastOrders: '/past-orders',
};

export const resources = {
  account: 'account',
  menuItem: 'menuItem',
  menuCategory: 'menuCategory',
  menu: 'menu',
  establishment: 'establishment',
  location: 'location',
  presetOptionGroup: 'presetOptionGroup',
  timeSlot: 'timeSlot',
  promotion: 'promotion',
};

export const FOOD_ATTRIBUTES: string[] = [
  'healthy',
  'alcoholic',
  'vegetarian',
  'vegan',
  'halal',
  'kosher',
  'glutenFree',
  'dairyFree',
  'peanutFree',
  'spicy',
];

export const FOOD_VALUES: string[] = [
  'caloriesExplicit',
  'displayPrice',
  'serves',
  'temperature',
];

export const LOCALES = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsche',
  es: 'Español',
  zh: '简体中文',
  fi: 'Finnish',
  sv: 'Swedish',
};

function createFoodAttrProperties() {
  function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  const ret: any = {};
  FOOD_ATTRIBUTES.forEach((f: string) => {
    ret['is' + capitalizeFirstLetter(f)] = false;
  });
  return ret;
}

function createFoodValueProperties(defaultVal: any) {
  const ret: any = {};
  FOOD_VALUES.forEach((f: string) => {
    ret[f] = defaultVal;
  });
  return ret;
}

export const EMPTY_SUBSCRIPTION: Subscription = {
  docId: '',
  ownerId: '',
  isActive: false,
  stripeAccount: null,
};

export const EMPTY_MENU: Menu = {
  docId: '',
  name: '',
  timeSlots: [],
  isVisible: true,
  categories: [],
  images: [],
};

export const EMPTY_MENU_CATEGORY: MenuCategory = {
  docId: '',
  name: '',
  timeSlots: [],
  isVisible: true,
  description: '',
  menuItems: [],
  price: 0,
  images: [],
};

export const EMPTY_MENU_ITEM: MenuItem = {
  docId: '',
  timeSlots: [],
  isVisible: true,
  name: '',
  secondaryName: '',
  description: '',
  price: 0,
  minimumQuantity: 1,
  combinedOptionGroups: [],
  presetOptionGroups: [],
  customOptionGroups: [],
  temperature: null,
  caloriesExplicit: null,
  expectedWaitTime: null,
  isBeverage: false,
  serves: null,
  preciseSizeValue: 0,
  preciseSizeUnit: Enums.eMeasurementUnits.g,
  images: [],
  ...createFoodAttrProperties(),
  ...createFoodValueProperties(null),
};

export const EMPTY_ESTABLISHMENT: Establishment = {
  docId: '',
  name: '',
  menus: [],
  primaryColor: [],
  accentColor: [],
  fontColorOnPrimary: [],
  fontColorOnAccent: [],
  primaryFont: 'Roboto',
  primaryFontVariant: 'regular',
  primaryFontScale: 1,
  secondaryFont: 'Oswald',
  secondaryFontVariant: 'regular',
  secondaryFontScale: 1,
  priceDisplayFormat: Enums.ePriceDisplayFormats.minDecimal,
  secondaryFontUppercase: false,
  showPwaPromptImmediately: false,
  description: '',
  images: [],
};

export const EMPTY_LOCATION: EstablishmentLocation = {
  docId: '',
  email: '',
  phoneNumber: '',
  formattedAddress: '',
  address: {
    administrativeAreaLevel1: '',
    administrativeAreaLevel2: '',
    country: '',
    locality: '',
    postalCode: '',
    route: '',
    streetNumber: '',
  } as GoogleAddress,
  locationId: -1,
  latitude: null,
  longitude: null,
  googlePlaceId: '',
  utcOffset: new Date().getTimezoneOffset(),
  establishment: null,
  numberOfStations: 1,
  jsonFeedEnabled: false,
  promotion: null,
  tablesideTimeSlots: [],
  takeoutTimeSlots: [],
  deliveryTimeSlots: [],
  isTablesideOn: false,
  isTakeoutOn: false,
  isDeliveryOn: false,
  orderAheadEnabled: false,
  sendEmailsForOpenHours: false,
  maximumDeliveryOrderDistance: 10,
  minimumDeliveryOrderAmount: 1000,
  deliveryChargeAmount: 0,
  closureDates: [],
  showLocationWarning: false,
  enableOrderPrePayingForTableside: false,
  enableOrderWithoutPayingForTakeout: false,
  enableOrderWithoutPayingForDelivery: false,
};

export const EMPTY_OPTION_GROUP_PRESET: OptionGroup = {
  docId: '',
  name: '',
  numberOfSelections: 0,
  mustPickExactAmount: false,
  canSelectMultipleOfSameOption: false,
  options: [],
  isVisible: false,
};

export const EMPTY_FILTER_OBJECT = {
  showIndeterminate: false,
  selectedAttributes: {}, // Do not prepopulate this. Having values here mean they're pertinant based on the current menu item selection.
  highestValues: {
    ...createFoodValueProperties(0),
  },
  lowestValues: {
    ...createFoodValueProperties(Number.MAX_SAFE_INTEGER),
  },
  ranges: {
    ...createFoodValueProperties([0, Number.MAX_SAFE_INTEGER]),
  },
};

export const UNINITIALIZED_EXCHANGE_OBJ_STATION_NUMBER = -1;

export const EMPTY_USER_ESTABLISHMENT_ECHANGE_OBJ: UserEstablishmentExchangeObject = {
  docId: '',
  stationId: UNINITIALIZED_EXCHANGE_OBJ_STATION_NUMBER,
  takeOutStationId: UNINITIALIZED_EXCHANGE_OBJ_STATION_NUMBER,
  deliveryStationId: UNINITIALIZED_EXCHANGE_OBJ_STATION_NUMBER,
  tablesideStationId: UNINITIALIZED_EXCHANGE_OBJ_STATION_NUMBER,
  verifiedPayments: {},
  verifiedAuthorizations: {},
  verifiedCancellations: {},
  deliveryOrderCollection: {},
  takeoutOrderCollection: {},
  tablesideOrderCollection: {},
  pendingBills: [],
  acceptedBills: [],
  rejectedBills: [],
  cancelledBills: [],
  liked: [],
  disliked: [],
  notificationsToTheEstablishment: [],
  notificationsToTheUser: [],
  userNotificationToken: '',
  pushNotificationMessage: null,
  serverReview: null,
  experienceReview: null,
  referenceName: '',
  contactEmail: '',
  contactNumber: null,
  isFirstTimeAtLocation: false,
  tablesideViewedCounts: {},
  takeoutViewedCounts: {},
  deliveryViewedCounts: {},
};

export const EMPTY_CLIENT_USER: ClientUser = {
  displayName: '',
  photoURL: '',
  email: '',
  deliveryAddress: null,
  highestConfirmedAge: -1,
  settings: {
    locale: 'en',
    contactEmail: '',
    contactNumber: null,
    referenceName: '',
    includeTaxWithPrices: false,
    showImagesAutomatically: true,
  },
};

export const COUNTRY_CODES = ['de', 'es', 'fr', 'zh', 'en'];

export const JSON_FEED_BASE = {
  version: 'https://jsonfeed.org/version/1',
  title: '',
  home_page_url: '',
  feed_url: '',
  items: [],
};
